import { Link } from "react-router-dom";

import { AppCard, AppCardBody, AppText, kindredFontFamilies } from "../../ui-foundation";
import {
  DocumentList,
  DocumentPageScaffold,
  DocumentSection,
  DocumentSubsection,
} from "@/components/DocumentScaffold";
import {
  KINDRED_BREACH_RESPONSE_PROCESS_BULLETS,
  KINDRED_BREACH_REPORTING_COPY,
  KINDRED_COMPLAINT_HANDLING_BULLETS,
  KINDRED_DATA_PROTECTION_ESCALATION_BULLETS,
  KINDRED_DATA_PROTECTION_ESCALATION_LEAD,
  KINDRED_COUNTRY_ANNEX_PRIVACY_BULLETS,
  KINDRED_DATA_RIGHTS_REGION_BULLETS,
  KINDRED_DATA_RIGHTS_SCOPE_COPY,
  KINDRED_EEA_REPRESENTATIVE_COPY,
  KINDRED_PRIVACY_LEAD_CONTACT_COPY,
  KINDRED_FORMAL_REQUEST_PROCESS_BULLETS,
  KINDRED_INTERNATIONAL_TRANSFERS_COPY,
  KINDRED_NO_SALE_OR_AD_MARKETING_SHARE_COPY,
  KINDRED_PRIVACY_DATA_RIGHTS_LABEL,
  KINDRED_PRIVACY_REQUEST_HANDLING_BULLETS,
  KINDRED_RETENTION_SCHEDULE_BULLETS,
  KINDRED_SERVICE_PROVIDER_DISCLOSURE_COPY,
  KINDRED_SERVICE_COMMUNICATIONS_COPY,
  KINDRED_SUPPORT_EMAIL,
  STEADY_HOUSE_ICO_REGISTRATION_REFERENCE,
  STEADY_HOUSE_SUPPORT_URL,
} from "../../shared/support";

const Privacy = () => {
  return (
    <DocumentPageScaffold
      title="Privacy Policy"
      updatedLabel="Last updated: July 2026"
      fallbackPath="/"
    >
      <DocumentSection title="1. Overview">
        <AppText>
          This Privacy Policy explains what personal data Kindred collects, how we use it, how we handle
          complaints and data incidents, and the choices available to members. The separate{" "}
          <Link to="/terms" className="kindred-inline-link">
            Terms &amp; Conditions
          </Link>{" "}
          explain the wider rules for using the app.
        </AppText>
      </DocumentSection>

      <DocumentSection title="2. Data Controller">
        <AppText>
          Steady House Ltd, operating Kindred Community, is the data controller for personal data collected
          through Kindred.
        </AppText>
        <AppText>
          Steady House Ltd is registered with the UK Information Commissioner&apos;s Office (ICO),
          registration reference <strong>{STEADY_HOUSE_ICO_REGISTRATION_REFERENCE}</strong>.
        </AppText>
        <AppText>{KINDRED_PRIVACY_LEAD_CONTACT_COPY}</AppText>
        <AppText>{KINDRED_EEA_REPRESENTATIVE_COPY}</AppText>
      </DocumentSection>

      <DocumentSection title="3. Data We Collect">
        <DocumentList
          items={[
            "Account details such as your email address and encrypted authentication records",
            "Profile information such as your display name, home country, postcode or equivalent locality code, bio, profile photo, and what you are looking for on Kindred",
            "Children's birth years where you choose to add them",
            "Content you create such as posts, replies, messages, chat room activity, adventures, and event RSVPs",
            "Operational data such as notifications, moderation records, and service activity needed to keep the community safe",
            "Usage and product-interaction data such as screen views, support-card opens, social-link clicks, and feature activity",
            "Device and diagnostic data such as push-notification tokens, crash reports, error logs, and basic app or device information needed to keep Kindred working",
          ]}
        />
      </DocumentSection>

      <DocumentSection title="4. How We Use Your Data">
        <DocumentList
          items={[
            "To create and maintain your Kindred account",
            "To show your profile and content to the right members inside the app",
            "To help members find someone to tag: an exact account email or account name may be matched privately, while suggestions show only the member's public display name and profile photo",
            "To run community features such as messages, chat rooms, adventures, and events",
            "To send service notifications, onboarding guidance, and support responses",
            "To understand which features are useful and where the app needs to be simpler or more reliable",
            "To diagnose crashes, fix errors, deliver push notifications, and maintain the app securely",
            "To keep the platform safe, prevent misuse, investigate incidents, and meet legal obligations",
          ]}
        />
      </DocumentSection>

      <DocumentSection title="5. Legal Basis For Processing">
        <DocumentList
          items={[
            "Contract, where we need your data to provide the Kindred service you signed up for",
            "Legitimate interests, where we need to keep Kindred safe, stable, and useful for members",
            "Consent, where you choose to use optional features or give us specific permission",
            "Legal obligation, where we must retain or disclose limited data to comply with the law",
          ]}
        />
      </DocumentSection>

      <DocumentSection title="6. How We Share Data">
        <DocumentSubsection title="6.1 With Other Members">
          <AppText>
            Other members can see the profile fields and content you choose to share inside Kindred, such as
            your display name, general location, posts, replies, adventures, chat messages, and event
            participation.
          </AppText>
        </DocumentSubsection>

        <DocumentSubsection title="6.2 With Service Providers">
          <AppText>{KINDRED_SERVICE_PROVIDER_DISCLOSURE_COPY}</AppText>
        </DocumentSubsection>

        <DocumentSubsection title="6.3 No Sale Or Advertising Sharing">
          <AppText>{KINDRED_NO_SALE_OR_AD_MARKETING_SHARE_COPY}</AppText>
        </DocumentSubsection>

        <DocumentSubsection title="6.4 Legal Or Safety Disclosure">
          <AppText>
            We may disclose limited information where required by law, safeguarding, fraud prevention, or
            community safety.
          </AppText>
        </DocumentSubsection>
      </DocumentSection>

      <DocumentSection title="7. Retention Schedule">
        <DocumentList items={[...KINDRED_RETENTION_SCHEDULE_BULLETS]} />
      </DocumentSection>

      <DocumentSection title="8. Your Rights">
        <AppText>{KINDRED_DATA_RIGHTS_SCOPE_COPY}</AppText>
        <DocumentList items={[...KINDRED_DATA_RIGHTS_REGION_BULLETS]} />

        <DocumentSubsection title="8.1 Right To Access">
          <AppText>
            You can use <strong>Menu &gt; Settings &gt; Delete account</strong> to download a JSON export of the
            personal data currently linked to your account.
          </AppText>
        </DocumentSubsection>

        <DocumentSubsection title="8.2 Right To Erasure">
          <AppText>
            You can use <strong>Menu &gt; Settings &gt; Delete account</strong> to request account deletion. Once
            the request is approved, Kindred automatically erases the linked account data and removes the auth
            account.
          </AppText>
        </DocumentSubsection>

        <DocumentSubsection title="8.3 Rectification And Help">
          <AppText>
            Update the profile details you control inside the app where possible. If you need help with
            access, correction, deletion, or a more formal privacy request, use the{" "}
            <a
              href={STEADY_HOUSE_SUPPORT_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="kindred-inline-link"
            >
              Steady House support page
            </a>{" "}
            or email{" "}
            <a href={`mailto:${KINDRED_SUPPORT_EMAIL}`} className="kindred-inline-link">
              {KINDRED_SUPPORT_EMAIL}
            </a>
            .
          </AppText>
          <AppText>
            For a plain-English summary of these routes, see the{" "}
            <Link to="/data-rights" className="kindred-inline-link">
              {KINDRED_PRIVACY_DATA_RIGHTS_LABEL}
            </Link>{" "}
            page.
          </AppText>
        </DocumentSubsection>
      </DocumentSection>

      <DocumentSection title="9. Service And Marketing Communications">
        <AppText>{KINDRED_SERVICE_COMMUNICATIONS_COPY}</AppText>
      </DocumentSection>

      <DocumentSection title="10. Security">
        <AppText>
          We use appropriate technical and organisational measures to protect member data, including encrypted
          password storage, HTTPS, access controls, and review of privileged actions.
        </AppText>
      </DocumentSection>

      <DocumentSection title="11. Session And Device Safety">
        <DocumentList
          items={[
            "Kindred offers a Keep me logged in choice. If you leave it off, web sessions stay browser-session scoped and mobile sessions stay device-memory scoped rather than restoring indefinitely.",
            "If you turn it on, Kindred stores the session on your device so you stay signed in between visits.",
            "Sign out after using a shared device, and contact support if you think someone else accessed your account.",
            "Sensitive admin and moderator actions may still require a fresh authenticator step-up for that session.",
          ]}
        />
      </DocumentSection>

      <DocumentSection title="12. International Transfers">
        <AppText>{KINDRED_INTERNATIONAL_TRANSFERS_COPY}</AppText>
        <AppText>{KINDRED_EEA_REPRESENTATIVE_COPY}</AppText>
      </DocumentSection>

      <DocumentSection title="13. Children's Privacy And Photos">
        <AppText>
          Kindred is for adults aged 18 and over. We do not knowingly offer accounts to children, and child
          information added by members is limited to birth-year level data only.
        </AppText>
        <DocumentList
          items={[
            "Only upload a photo of a child if you are their parent or carer, or you otherwise have clear permission to share it.",
            "Do not upload identifiable photos of another member's child without that parent or carer's agreement.",
            "Avoid posting school uniforms, school names, home addresses, vehicle number plates, or live location clues in child photos.",
            "Kindred photo surfaces are intended for signed-in member use inside the app and are not published openly on the public web by default.",
          ]}
        />
      </DocumentSection>

      <DocumentSection title="14. Cookies And Local Storage">
        <AppText>
          We use essential cookies and local storage to keep members signed in and the app working properly.
          Kindred does not use advertising cookies.
        </AppText>
      </DocumentSection>

      <DocumentSection title="15. Changes To This Policy">
        <AppText>
          We may update this Privacy Policy from time to time. Significant changes will be communicated inside
          the app or through the contact routes that are appropriate for the update.
        </AppText>
      </DocumentSection>

      <DocumentSection title="16. Contact, Complaints, And Breach Reporting">
        <AppText>
          For privacy questions, formal requests, support with access or erasure, or complaints about how
          personal data is handled, use the{" "}
          <a
            href={STEADY_HOUSE_SUPPORT_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="kindred-inline-link"
          >
            Steady House support page
          </a>{" "}
          or email{" "}
          <a href={`mailto:${KINDRED_SUPPORT_EMAIL}`} className="kindred-inline-link">
            {KINDRED_SUPPORT_EMAIL}
          </a>
          .
        </AppText>
        <AppText>{KINDRED_BREACH_REPORTING_COPY}</AppText>
        <DocumentSubsection title="16.1 Formal request handling">
          <DocumentList items={[...KINDRED_FORMAL_REQUEST_PROCESS_BULLETS, ...KINDRED_PRIVACY_REQUEST_HANDLING_BULLETS]} />
        </DocumentSubsection>
        <DocumentSubsection title="16.2 Complaint handling">
          <DocumentList items={[...KINDRED_COMPLAINT_HANDLING_BULLETS]} />
        </DocumentSubsection>
        <DocumentSubsection title="16.3 Breach response">
          <DocumentList items={[...KINDRED_BREACH_RESPONSE_PROCESS_BULLETS]} />
        </DocumentSubsection>
        <AppText>{KINDRED_DATA_PROTECTION_ESCALATION_LEAD}</AppText>
        <DocumentList items={[...KINDRED_DATA_PROTECTION_ESCALATION_BULLETS]} />
      </DocumentSection>

      <DocumentSection title="17. Country Annexes">
        <DocumentList items={[...KINDRED_COUNTRY_ANNEX_PRIVACY_BULLETS]} />
      </DocumentSection>

      <AppCard backgroundColor="$panelBackground">
        <AppCardBody alignItems="center">
          <AppText
            textAlign="center"
            fontFamily={kindredFontFamilies.sansBold}
            fontSize="$md"
            lineHeight="$md"
          >
            Kindred keeps privacy, data-rights, and complaint routes visible in product so members can act in
            app first and reach human support when an issue needs review.
          </AppText>
        </AppCardBody>
      </AppCard>
    </DocumentPageScaffold>
  );
};

export default Privacy;
